import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Shield, Users, Award, MapPin, Calendar, Heart, Lightbulb, UserCheck, CheckCircle } from 'lucide-react';
import { companyInfo, teamMembers, companyValues, stats, vision, foundingVision, aboutCompany } from '../data/mock';

const AboutPage = () => {
  const valueIcons = [Target, Heart, UserCheck, Lightbulb, Shield, Users];

  return (
    <main className="pt-20 sm:pt-24">
      {/* Hero Section */}
      <section className="relative py-16 sm:py-24 bg-slate-50 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1920&q=80"
            alt="Construction background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-white/80" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6 animate-fade-in">
              <div className="h-1 w-12 sm:w-16 bg-yellow-500" />
              <span className="text-yellow-600 font-bold tracking-wider text-xs sm:text-sm uppercase">About Us</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 leading-tight mb-4 sm:mb-6 animate-fade-in-up animation-delay-200 uppercase tracking-tight">
              Building Cape Town's Future Since 2013
            </h1>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed animate-fade-in-up animation-delay-400">
              {aboutCompany.intro} {aboutCompany.history}
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 sm:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative">
                <div className="aspect-[4/3] rounded-sm overflow-hidden shadow-xl">
                  <img
                    src="https://customer-assets.emergentagent.com/job_87a42c2d-b3ff-4965-9d7d-e392365dc691/artifacts/4q0h5loo_WhatsApp%20Image%202026-02-11%20at%205.56.11%20AM.jpeg"
                    alt="Barchalo Construction Site"
                    className="w-full h-full object-cover"
                  />
                  <img src={companyInfo.logo} alt="Barchalo" className="absolute bottom-2 right-2 h-10 w-auto opacity-80" />
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-4 mb-4">
                <div className="h-1 w-12 bg-yellow-500" />
                <span className="text-yellow-600 font-bold tracking-wider text-sm uppercase">Our Story</span>
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-6 uppercase tracking-tight">
                Four Decades of Building Excellence
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>{aboutCompany.approach}</p>
                <p>{aboutCompany.safety}</p>
              </div>

              {/* Company Details */}
              <div className="mt-8 p-6 bg-slate-50 rounded-sm border-l-4 border-yellow-500">
                <h4 className="text-slate-900 font-bold mb-4">Company Details</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Registration No:</span>
                    <p className="text-slate-700 font-medium">{companyInfo.registrationNumber}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">VAT Status:</span>
                    <p className="text-slate-700 font-medium">VAT Compliant</p>
                  </div>
                  <div>
                    <span className="text-slate-500">BIBC No:</span>
                    <p className="text-slate-700 font-medium">{companyInfo.bibcNumber}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Registered:</span>
                    <p className="text-slate-700 font-medium">{companyInfo.bibcRegistrationDate}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="h-1 w-12 bg-yellow-500" />
              <span className="text-yellow-600 font-bold tracking-wider text-sm uppercase">Our Vision</span>
              <div className="h-1 w-12 bg-yellow-500" />
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-8 uppercase tracking-tight">
              Leading Through Excellence
            </h2>
            <p className="text-xl text-slate-700 leading-relaxed mb-12">
              "{vision}"
            </p>

            {/* Founding Vision */}
            <div className="grid md:grid-cols-2 gap-6">
              {foundingVision.map((item) => (
                <div key={item} className="p-6 bg-white rounded-sm border border-slate-100 shadow-sm text-left">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-sm bg-yellow-500 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-slate-900" />
                    </div>
                    <p className="text-slate-600">{item}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="h-1 w-12 bg-yellow-500" />
              <span className="text-yellow-600 font-bold tracking-wider text-sm uppercase">Our Values</span>
              <div className="h-1 w-12 bg-yellow-500" />
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 uppercase tracking-tight">What We Stand For</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our core values guide every decision we make and every project we undertake.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {companyValues.map((value, index) => {
              const IconComponent = valueIcons[index] || Target;
              return (
                <div
                  key={value.id}
                  className="p-8 bg-white border border-slate-100 rounded-sm group hover:border-yellow-500/50 transition-all duration-300 shadow-sm hover:shadow-lg"
                >
                  <div className="w-14 h-14 rounded-sm bg-yellow-500/10 flex items-center justify-center mb-6 group-hover:bg-yellow-500 transition-colors duration-300">
                    <IconComponent className="w-7 h-7 text-yellow-600 group-hover:text-slate-900 transition-colors duration-300" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-slate-900 mb-3 uppercase">{value.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="h-1 w-12 bg-yellow-500" />
              <span className="text-yellow-600 font-bold tracking-wider text-sm uppercase">Our Team</span>
              <div className="h-1 w-12 bg-yellow-500" />
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 uppercase tracking-tight">Meet the Leadership</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our experienced team brings decades of combined expertise to every project.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {teamMembers.map((member) => (
              <div
                key={member.id}
                className="group bg-white rounded-sm overflow-hidden border border-slate-100 hover:border-yellow-500/50 transition-all duration-300 shadow-sm hover:shadow-lg"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-slate-100 flex items-center justify-center">
                  {member.image ? (
                    <img 
                      src={member.image} 
                      alt={member.name}
                      className="w-full h-full object-cover"
                      style={{ 
                        objectPosition: member.id === 3 ? 'center 25%' : 
                                       member.id === 5 ? 'center 50%' : 
                                       member.id === 6 ? 'center top' : 'top',
                        transform: member.id === 6 ? 'scale(2.2) translateY(25%)' : 'none'
                      }}
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-sm bg-yellow-500/10 flex items-center justify-center">
                      <Users className="w-12 h-12 text-yellow-500/50" />
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-1">{member.name}</h3>
                  <p className="text-yellow-600 text-sm font-bold mb-3 uppercase">{member.role}</p>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">{member.bio}</p>
                  <a 
                    href={`tel:${member.phone}`}
                    className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-yellow-600 transition-colors"
                  >
                    <span className="w-8 h-8 rounded-sm bg-yellow-500/10 flex items-center justify-center">
                      <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </span>
                    {member.phone}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 sm:gap-16 items-center">
            <div>
              <div className="flex items-center gap-4 mb-4">
                <div className="h-1 w-12 bg-yellow-500" />
                <span className="text-yellow-600 font-bold tracking-wider text-sm uppercase">Experience</span>
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-6 uppercase tracking-tight">
                Areas We Serve
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                Based in Parow East, we serve clients across the Western Cape and have completed projects throughout 
                South Africa. Our reach extends wherever quality construction is needed.
              </p>

              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-sm bg-yellow-500/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div>
                    <h4 className="text-slate-900 font-bold">Greater Cape Town</h4>
                    <p className="text-slate-600 text-sm">Parow, Century City, V&A Waterfront, and all suburbs</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-sm bg-yellow-500/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div>
                    <h4 className="text-slate-900 font-bold">Western Cape</h4>
                    <p className="text-slate-600 text-sm">Stellenbosch, Paarl, Franschhoek, Val de Vie, Langebaan</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-sm bg-yellow-500/10 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div>
                    <h4 className="text-slate-900 font-bold">Project Availability</h4>
                    <p className="text-slate-600 text-sm">Currently accepting new projects for 2026</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4 sm:gap-6">
              {stats.map((stat) => (
                <div
                  key={stat.id}
                  className="p-8 bg-slate-50 border-l-4 border-yellow-500 rounded-sm text-center"
                >
                  <div className="font-heading text-4xl font-bold text-yellow-600 mb-2">{stat.value}</div>
                  <div className="text-slate-600 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-16 sm:py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="h-1 w-12 bg-yellow-500" />
              <span className="text-yellow-500 font-bold tracking-wider text-sm uppercase">Accreditations</span>
              <div className="h-1 w-12 bg-yellow-500" />
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 uppercase tracking-tight">Certified & Compliant</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Fully registered and in good standing with South Africa's leading construction industry bodies.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* FEM Certificate */}
            <div className="relative bg-slate-800 rounded-sm border border-slate-700 overflow-hidden group hover:border-yellow-500/50 transition-all duration-500">
              <div className="relative p-8">
                <div className="flex items-start justify-between mb-6">
                  <div className="w-16 h-16 rounded-sm bg-yellow-500/10 flex items-center justify-center">
                    <Shield className="w-8 h-8 text-yellow-500" />
                  </div>
                  <div className="px-3 py-1 bg-green-500/20 rounded-sm">
                    <span className="text-green-400 text-xs font-bold uppercase">Valid</span>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-white mb-2">Letter of Good Standing</h3>
                <p className="text-yellow-500 font-bold mb-4">Federated Employers' Mutual Assurance Company (FEM)</p>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between items-center py-2 border-b border-slate-700">
                    <span className="text-slate-500 text-sm">Policy Number</span>
                    <span className="text-white font-medium">208237</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-700">
                    <span className="text-slate-500 text-sm">Inception Date</span>
                    <span className="text-white font-medium">01 June 2013</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-slate-500 text-sm">Issue Date</span>
                    <span className="text-white font-medium">01 August 2025</span>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-slate-900/50 rounded-sm">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-slate-300 text-sm">Verified compliant with all requirements</span>
                </div>
              </div>
            </div>

            {/* BIBC Certificate */}
            <div className="relative bg-slate-800 rounded-sm border border-slate-700 overflow-hidden group hover:border-yellow-500/50 transition-all duration-500">
              <div className="relative p-8">
                <div className="flex items-start justify-between mb-6">
                  <div className="w-16 h-16 rounded-sm bg-yellow-500/10 flex items-center justify-center">
                    <Award className="w-8 h-8 text-yellow-500" />
                  </div>
                  <div className="px-3 py-1 bg-green-500/20 rounded-sm">
                    <span className="text-green-400 text-xs font-bold uppercase">Registered</span>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-white mb-2">Certificate of Registration</h3>
                <p className="text-yellow-500 font-bold mb-4">Building Industry Bargaining Council (Cape of Good Hope)</p>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between items-center py-2 border-b border-slate-700">
                    <span className="text-slate-500 text-sm">Registration Number</span>
                    <span className="text-white font-medium">94342</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-700">
                    <span className="text-slate-500 text-sm">Registration Date</span>
                    <span className="text-white font-medium">11 June 2013</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-slate-500 text-sm">Issue Date</span>
                    <span className="text-white font-medium">06 August 2025</span>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-slate-900/50 rounded-sm">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-slate-300 text-sm">Registered Employer with BIBC</span>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Credentials */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-6 bg-slate-800 rounded-sm border border-slate-700 text-center">
              <p className="text-slate-500 text-xs mb-1">Company Reg.</p>
              <p className="text-white font-bold text-sm">2013/047610/07</p>
            </div>
            <div className="p-6 bg-slate-800 rounded-sm border border-slate-700 text-center">
              <p className="text-slate-500 text-xs mb-1">VAT Status</p>
              <p className="text-white font-bold text-sm">VAT Compliant</p>
            </div>
            <div className="p-6 bg-slate-800 rounded-sm border border-slate-700 text-center">
              <p className="text-slate-500 text-xs mb-1">BIBC Number</p>
              <p className="text-white font-bold text-sm">94342</p>
            </div>
            <div className="p-6 bg-slate-800 rounded-sm border border-slate-700 text-center">
              <p className="text-slate-500 text-xs mb-1">Established</p>
              <p className="text-white font-bold text-sm">June 2013</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-yellow-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-4 uppercase tracking-tight">
            Ready to Work With Us?
          </h2>
          <p className="text-slate-800 mb-8 max-w-2xl mx-auto">
            Let's discuss your project and see how we can bring your vision to life.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-slate-900 text-white font-bold uppercase tracking-wide rounded-sm hover:bg-slate-800 transition-colors duration-300"
          >
            Get in Touch
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
